import { skipToken } from "@reduxjs/toolkit/query";
import {
  checkFunnelUiVisible,
  checkFunnelUiWritable,
} from "@repo/app-types/entities/funnel";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { useVoiceAgentFunnelsQuery } from "../../../api/entities/funnel.js";
import { useFeatureFlagVariant } from "../../../helpers/featureFlags/hooks.js";

export interface FunnelView {
  /**
   * Whether the agent's Performance tab offers the Funnel view: always where
   * funnels are writable, and where they're read-only only on an agent that
   * has a funnel.
   */
  isShown: boolean;
  /** Whether the Funnel view is the tab's default: shown, on an agent with a funnel. */
  isDefault: boolean;
  /** Whether the agent's funnels are still loading, so `isDefault` isn't known yet. */
  isLoading: boolean;
}

/** The Funnel view of a static agent's Performance tab; legacy agents have none. */
export function useFunnelView(
  teammateAgentId: TeammateId,
  isStaticAgent: boolean,
): FunnelView {
  const funnelUiVariant = useFeatureFlagVariant(
    FeatureFlagKey.VoiceAgentFunnelUi,
  );
  const isFunnelUiVisible =
    isStaticAgent && checkFunnelUiVisible(funnelUiVariant);
  const { data: funnels, isLoading } = useVoiceAgentFunnelsQuery(
    isFunnelUiVisible ? teammateAgentId : skipToken,
  );
  const hasFunnel = Boolean(funnels?.length);
  const isShown =
    isFunnelUiVisible && (checkFunnelUiWritable(funnelUiVariant) || hasFunnel);
  return { isShown, isDefault: isShown && hasFunnel, isLoading };
}
